import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const labelDescriptionStart = 'Label for field: ';
const displayPathStart = 'Catalogs|Benefits|Details|';
const sharedLocProperties: Pick<LocDef, 'htmlAllowed' | 'shared' | 'portal' | 'feature'> = {
	htmlAllowed: false,
	shared: false,
	portal: 'SP',
	feature: HubFeature.Benefit,
};

const grantTypeHintDefault: string =
	'The way in which a benefit is granted. Once = The benefit is only granted once ever. Once per certification = The benefit is granted once per certification. Once per event = The benefit is granted each time a candidate achieves initial certification (either the first time or after having a certification expire and re-earning it). Once per logic = The benefit is granted each time a candidate achieves or extends a certification (this includes initial certification and subsequent recertification events).';

const typeHintDefault: string =
	'The type of benefit offered. Download = A zip file a candidate can download (e.g., logos). Link = a URL the candidate is directed to for claiming a benefit. Individual Token = a unique token the candidate can use for a benefit. Global Token = a global token candidates can use for a benefit.';

export type BenefitDetailLocs =
	| 'SPBenefitDetailEditButton'
	| 'SPBenefitDetailCancelButton'
	| 'SPBenefitDetailUpdateButton'
	| 'SPBenefitDetailLabelGroup'
	| 'SPBenefitDetailLabelAbbreviation'
	| 'SPBenefitDetailLabelDataProvider'
	| 'SPBenefitDetailLabelType'
	| 'SPBenefitDetailLabelGrantType'
	| 'SPBenefitDetailHintGrantType'
	| 'SPBenefitDetailLabelName'
	| 'SPBenefitDetailLabelStartDate'
	| 'SPBenefitDetailLabelEndDate'
	| 'SPBenefitDetailLabelDisplayOrder'
	| 'SPBenefitDetailLabelClaimEndDate'
	| 'SPBenefitDetailLabelViewEndDate'
	| 'SPBenefitDetailContentForever'
	| 'SPBenefitDetailLabelActivePeriod'
	| 'SPBenefitDetailLabelApplyLink'
	| 'SPBenefitDetailLabelPreviewLink'
	| 'SPBenefitDetailLabelPinned'
	| 'SPBenefitDetailLabelGlobalToken'
	| 'SPBenefitDetailTitle'
	| 'SPBenefitDetailNameRequired'
	| 'SPBenefitDetailHintType'
	| 'SPBenefitDetailClaimEndDateOrder'
	| 'SPBenefitDetailClaimEndDateParse'
	| 'SPBenefitDetailDisplayOrderPattern'
	| 'SPBenefitDetailHintActivePeriod'
	| 'SPBenefitDetailHintApplyLink'
	| 'SPBenefitDetailErrorApplyLink'
	| 'SPBenefitDetailHintPinned'
	| 'SPBenefitDetailNoticeChangesSaved';

export const spBenefitDetailLocs: LocDefs<BenefitDetailLocs> = {
	SPBenefitDetailTitle: {
		defaultValue: 'Benefit Details',
		displayType: 'Title',
		description: 'Title for the benefit details page.',
		displayPath: `${displayPathStart}Title`,
		legacyName: 'Benefit/Catalogs.BenefitDetails.Title.PageTitle',
		...sharedLocProperties,
	},
	SPBenefitDetailEditButton: {
		defaultValue: 'Edit',
		displayType: 'Button',
		description: 'Edit button above the form.',
		displayPath: `${displayPathStart}Edit`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.Button.Edit',
		...sharedLocProperties,
	},
	SPBenefitDetailCancelButton: {
		defaultValue: 'Cancel',
		displayType: 'Button',
		description: 'Cancel button above the form.',
		displayPath: `${displayPathStart}Cancel`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.Button.Cancel',
		...sharedLocProperties,
	},
	SPBenefitDetailUpdateButton: {
		defaultValue: 'Update',
		displayType: 'Button',
		description: 'Update button for the form.',
		displayPath: `${displayPathStart}Update`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.Button.Update',
		...sharedLocProperties,
	},
	SPBenefitDetailLabelGroup: {
		defaultValue: 'Group',
		displayType: 'Label',
		description: `${labelDescriptionStart}Group.`,
		displayPath: `${displayPathStart}Update`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.FieldLabel.Group',
		...sharedLocProperties,
	},
	SPBenefitDetailLabelName: {
		defaultValue: 'Name',
		displayType: 'Label',
		description: `${labelDescriptionStart}Name.`,
		displayPath: `${displayPathStart}Name`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.FieldLabel.Name',
		...sharedLocProperties,
	},
	SPBenefitDetailLabelAbbreviation: {
		defaultValue: 'Abbreviation',
		displayType: 'Label',
		description: `${labelDescriptionStart}Abbreviation.`,
		displayPath: `${displayPathStart}Abbreviation`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.FieldLabel.Abbreviation',
		...sharedLocProperties,
	},
	SPBenefitDetailLabelDataProvider: {
		defaultValue: 'Data Provider',
		displayType: 'Label',
		description: `${labelDescriptionStart}Data Provider.`,
		displayPath: `${displayPathStart}Data Provider`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.FieldLabel.DataProvider',
		...sharedLocProperties,
	},
	SPBenefitDetailLabelType: {
		defaultValue: 'Type',
		displayType: 'Label',
		description: `${labelDescriptionStart}Type.`,
		displayPath: `${displayPathStart}Type`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.FieldLabel.Type',
		...sharedLocProperties,
	},
	SPBenefitDetailLabelGrantType: {
		defaultValue: 'Grant Type',
		displayType: 'Label',
		description: `${labelDescriptionStart}Grant Type.`,
		displayPath: `${displayPathStart}Grant Type`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.FieldLabel.GrantType',
		...sharedLocProperties,
	},
	SPBenefitDetailHintGrantType: {
		defaultValue: grantTypeHintDefault,
		displayType: 'Content',
		description: `Content for the hint shown for grant type.`,
		displayPath: `${displayPathStart}Grant Type Hint`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.FieldHint.GrantType',
		...sharedLocProperties,
	},
	SPBenefitDetailLabelStartDate: {
		defaultValue: 'Start Date',
		displayType: 'Label',
		description: `${labelDescriptionStart}Start date.`,
		displayPath: `${displayPathStart}Start Date`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.FieldLabel.StartDate',
		...sharedLocProperties,
	},
	SPBenefitDetailLabelEndDate: {
		defaultValue: 'End Date',
		displayType: 'Label',
		description: `${labelDescriptionStart}End date.`,
		displayPath: `${displayPathStart}End Date`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.EndDate',
		...sharedLocProperties,
	},
	SPBenefitDetailLabelClaimEndDate: {
		defaultValue: 'Claim End Date',
		displayType: 'Label',
		description: `${labelDescriptionStart}Claim End date.`,
		displayPath: `${displayPathStart}Claim End Date`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.ClaimEndDate',
		...sharedLocProperties,
	},
	SPBenefitDetailLabelViewEndDate: {
		defaultValue: 'View End Date',
		displayType: 'Label',
		description: `${labelDescriptionStart}View End date.`,
		displayPath: `${displayPathStart}View End Date`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.ViewEndDate',
		...sharedLocProperties,
	},
	SPBenefitDetailLabelDisplayOrder: {
		defaultValue: 'Display Order',
		displayType: 'Label',
		description: `${labelDescriptionStart}Display Order.`,
		displayPath: `${displayPathStart}Display Order`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.FieldLabel.DisplayOrder',
		...sharedLocProperties,
	},
	SPBenefitDetailLabelActivePeriod: {
		defaultValue: 'Active Period',
		displayType: 'Label',
		description: `${labelDescriptionStart}Active Period`,
		displayPath: `${displayPathStart}Active Period`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.FieldLabel.ActivePeriod',
		...sharedLocProperties,
	},
	SPBenefitDetailContentForever: {
		defaultValue: 'Forever',
		displayType: 'Content',
		description: `Content displayed as 'Forever'.`,
		displayPath: `${displayPathStart}Forever`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.Content.Forever',
		...sharedLocProperties,
	},
	SPBenefitDetailLabelApplyLink: {
		defaultValue: 'Apply Link',
		displayType: 'Label',
		description: `${labelDescriptionStart}Apply Link.`,
		displayPath: `${displayPathStart}Apply Link`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.FieldLabel.ApplyLink',
		...sharedLocProperties,
	},
	SPBenefitDetailLabelPreviewLink: {
		defaultValue: 'Preview Link',
		displayType: 'Button',
		description: 'Button for the preview link.',
		displayPath: `${displayPathStart}Preview Link`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.FieldLabel.PreviewLink',
		...sharedLocProperties,
	},
	SPBenefitDetailLabelPinned: {
		defaultValue: 'Pinned',
		displayType: 'Label',
		description: `${labelDescriptionStart}Pinned.`,
		displayPath: `${displayPathStart}Pinned`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.FieldLabel.Pinned',
		...sharedLocProperties,
	},
	SPBenefitDetailLabelGlobalToken: {
		defaultValue: 'Global Token',
		displayType: 'Label',
		description: `${labelDescriptionStart}Global Token.`,
		displayPath: `${displayPathStart}Global Token`,
		legacyName: 'Benefit/Catalogs.BenefitsDetails.FieldLabel.GlobalToken',
		...sharedLocProperties,
	},
	SPBenefitDetailNameRequired: {
		defaultValue: 'Name is required',
		displayType: 'Error',
		description: `Error displaying that the name field is required`,
		displayPath: `${displayPathStart}Name Required`,
		legacyName: 'Benefit/Catalogs.BenefitsDetailsForm.FieldError.NameRequired',
		...sharedLocProperties,
	},
	SPBenefitDetailHintType: {
		defaultValue: typeHintDefault,
		displayType: 'Content',
		description: `Contents of the hint shown for field - Type.`,
		displayPath: `${displayPathStart}Type Hint`,
		legacyName: 'Benefit/Catalogs.BenefitsDetailsForm.FieldHint.Type',
		...sharedLocProperties,
	},
	SPBenefitDetailClaimEndDateOrder: {
		defaultValue: 'The claim end date must match or come before the view end date',
		displayType: 'Error',
		description: `Error shown when the picked claim end date is after the view end date.`,
		displayPath: `${displayPathStart} Claim End Date Order`,
		legacyName: 'Benefit/Catalogs.BenefitsDetailsForm.FieldError.ClaimEndDateOrder',
		...sharedLocProperties,
	},
	SPBenefitDetailClaimEndDateParse: {
		defaultValue: 'You must use a valid date',
		displayType: 'Error',
		description: `Error shown when the picked claim end date is not a valid date and cannot be parsed.`,
		displayPath: `${displayPathStart} Claim End Date Invalid`,
		legacyName: 'Benefit/Catalogs.BenefitsDetailsForm.FieldError.ClaimEndDateFormat',
		...sharedLocProperties,
	},
	SPBenefitDetailDisplayOrderPattern: {
		defaultValue: 'Display order must only contain numbers',
		displayType: 'Error',
		description: `Error shown when display order is invalid.`,
		displayPath: `${displayPathStart} Display Order Pattern`,
		legacyName: 'Benefit/Catalogs.BenefitsDetailsForm.FieldError.DisplayOrderNumeric',
		...sharedLocProperties,
	},
	SPBenefitDetailHintActivePeriod: {
		defaultValue:
			'The period of time (in months) a benefit is active once the cert is achieved (can be forever).',
		displayType: 'Content',
		description: `Contents of the hint shown for field - Active Period.`,
		displayPath: `${displayPathStart}Active Period Hint`,
		legacyName: 'Benefit/Catalogs.BenefitsDetailsForm.FieldHint.ActivePeriod',
		...sharedLocProperties,
	},
	SPBenefitDetailHintApplyLink: {
		defaultValue: `The URL with http:// or https:// that provides details about a benefit or is the benefit itself.`,
		displayType: 'Content',
		description: `Contents of the hint shown for field - Apply Link.`,
		displayPath: `${displayPathStart}Apply Link Hint`,
		legacyName: 'Benefit/Catalogs.BenefitsDetailsForm.FieldHint.Url',
		...sharedLocProperties,
	},
	SPBenefitDetailErrorApplyLink: {
		defaultValue: `Apply link must be a valid url`,
		displayType: 'Content',
		description: `Error shown when apply link is invalid.`,
		displayPath: `${displayPathStart}Apply Link Error`,
		legacyName: 'Benefit/Catalogs.BenefitsDetailsForm.FieldError.UrlFormat',
		...sharedLocProperties,
	},
	SPBenefitDetailHintPinned: {
		defaultValue:
			'If pinned, even though a certification is expired, the benefit will still be available for a candidate to claim.',
		displayType: 'Content',
		description: `Contents of the hint shown for field - Pinned.`,
		displayPath: `${displayPathStart}Pinned Hint`,
		legacyName: 'Benefit/Catalogs.BenefitsDetailsForm.FieldHint.Pinned',
		...sharedLocProperties,
	},
	SPBenefitDetailNoticeChangesSaved: {
		defaultValue: 'Changes saved',
		displayType: 'Notification',
		description: `Notification shown when changes are saved.`,
		displayPath: `${displayPathStart}Notice Changes Saved`,
		legacyName: 'Benefit/Catalogs.BenefitsDetailsForm.Notification.UpdateSuccess',
		...sharedLocProperties,
	},
};

export const LOC_KEYS = MapLocKeys<BenefitDetailLocs>(spBenefitDetailLocs);
export type SPBenefitDetailLocsType = LocMap<BenefitDetailLocs>;
